/**
 * 常驻静音循环音轨（原站 bg-audio 组件，src = noise.mp3）。
 *
 * 原站参数（实测自 commons 8702）：new Audio() + crossOrigin="anonymous"、
 * volume 0.5、loop、load()；autoPlay 时立即 play()，并额外挂一个一次性的
 * window click 监听再 play() 一次（绕开自动播放策略）。
 * 它没有任何 UI（原站 render 返回 null），isEnable 也从不改变 —— 整页生命周期内一直循环。
 */
import { useEffect, useRef } from 'react';

export const NOISE_SRC = '/assets/audio/noise.mp3';

/** 原站音量 */
const VOLUME = 0.5;

export function useLoopAudio(src: string = NOISE_SRC, enabled = true) {
  const ref = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    let cancelled = false;
    const tryPlay = () => {
      if (cancelled) return;
      void el.play().catch(() => {
        /* 自动播放被拦截：交给下面的手势监听 */
      });
    };

    el.load();
    tryPlay();

    // 原站挂的是 window click（一次性）
    const onGesture = () => {
      tryPlay();
      window.removeEventListener('click', onGesture);
    };
    window.addEventListener('click', onGesture, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener('click', onGesture);
    };
  }, [enabled, src]);

  return ref;
}

export function LoopAudio(props: { src?: string; enabled?: boolean }) {
  const ref = useLoopAudio(props.src, props.enabled ?? true);
  return (
    <audio
      ref={ref}
      src={props.src ?? NOISE_SRC}
      loop
      preload="auto"
      crossOrigin="anonymous"
      aria-hidden="true"
      onLoadedMetadata={(e) => {
        e.currentTarget.volume = VOLUME;
      }}
    />
  );
}
